import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Mail, Save, RotateCcw, Eye, Loader2, ArrowLeft, Tag, Check, Copy,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';

export default function SystemTemplates() {
  const { listSystemTemplates, getSystemTemplate, saveSystemTemplate, resetSystemTemplate, previewSystemTemplate, isAdmin, canAccess, loaded } = useAuth();
  const allowed = isAdmin || canAccess('admin-articles');
  const [list, setList] = useState([]);
  const [slug, setSlug] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingTpl, setLoadingTpl] = useState(false);

  useEffect(() => { listSystemTemplates().then((l) => { setList(l); if (l[0]) setSlug(l[0].slug); }).catch(() => {}); }, [listSystemTemplates]);

  const loadTpl = useCallback((s) => {
    setLoadingTpl(true); setPreview(null);
    getSystemTemplate(s).then((t) => { setTpl(t); setSubject(t.subject || ''); setBody(t.body || ''); }).finally(() => setLoadingTpl(false));
  }, [getSystemTemplate]);
  useEffect(() => { if (slug) loadTpl(slug); }, [slug, loadTpl]);

  const dirty = tpl && (subject !== tpl.subject || body !== tpl.body);

  const doPreview = async () => {
    setBusy(true);
    try { setPreview(await previewSystemTemplate(slug, { subject, body })); }
    catch (e) { toast.error('Preview failed.'); }
    finally { setBusy(false); }
  };
  const doSave = async () => {
    setBusy(true);
    try {
      const t = await saveSystemTemplate(slug, { subject, body });
      setTpl(t); setList((l) => l.map((x) => (x.slug === slug ? { ...x, is_custom: true } : x)));
      toast.success('Template saved. The live output now uses your version.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save.'); }
    finally { setBusy(false); }
  };
  const doReset = async () => {
    if (!window.confirm('Reset this template to the built-in default? Your customisations will be lost.')) return;
    setBusy(true);
    try {
      const t = await resetSystemTemplate(slug);
      setTpl(t); setSubject(t.subject); setBody(t.body); setPreview(null);
      setList((l) => l.map((x) => (x.slug === slug ? { ...x, is_custom: false } : x)));
      toast.success('Reset to the built-in default.');
    } catch (e) { toast.error('Could not reset.'); }
    finally { setBusy(false); }
  };

  const copyToken = (tok) => { navigator.clipboard?.writeText(`{{${tok}}}`); toast(`Copied {{${tok}}}`); };

  const emailCount = useMemo(() => list.filter((t) => t.category === 'email').length, [list]);

  if (loaded && !allowed) {
    return <div className="max-w-lg mx-auto py-24 text-center"><h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>Not authorised</h1><p className="mt-2" style={{ color: 'var(--olive)' }}>You need the Articles admin permission to edit system templates.</p></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 fade-in" data-testid="system-templates">
      <Link to="/admin/articles" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }} data-testid="st-back"><ArrowLeft size={15} /> Back to Articles</Link>
      <h1 className="font-title text-3xl md:text-4xl mt-3" style={{ color: 'var(--maroon)' }}>Report &amp; Output Templates</h1>
      <p className="text-sm mt-1 max-w-2xl" style={{ color: 'var(--olive)' }}>Edit the wording and layout of every emailed and printed output. Each uses mail-merge placeholders and falls back to a built-in default until you save your own.</p>

      <div className="grid md:grid-cols-[240px_1fr] gap-6 mt-6">
        {/* Template list */}
        <aside className="space-y-1.5" data-testid="st-list">
          {list.map((t) => {
            const Icon = t.category === 'email' ? Mail : FileText;
            const active = t.slug === slug;
            return (
              <button key={t.slug} onClick={() => setSlug(t.slug)} data-testid={`st-item-${t.slug}`}
                className="w-full text-left px-3 py-2 rounded-lg flex items-start gap-2 transition-colors"
                style={active ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', color: 'var(--navy)', border: '1px solid rgba(0,0,0,0.08)' }}>
                <Icon size={15} className="mt-0.5 flex-shrink-0" style={{ color: active ? 'var(--gold)' : 'var(--maroon)' }} />
                <span className="min-w-0">
                  <span className="block text-sm font-title leading-tight">{t.name}</span>
                  <span className="block text-[11px] leading-tight" style={{ opacity: 0.8 }}>{t.category === 'email' ? 'Email' : 'Document'}{t.is_custom ? ' · customised' : ''}</span>
                </span>
              </button>
            );
          })}
        </aside>

        {/* Editor */}
        <section>
          {loadingTpl || !tpl ? <div className="flex items-center gap-2 py-16" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={18} /> Loading…</div> : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-title text-xl" style={{ color: 'var(--navy)' }}>{tpl.name}</h2>
                  <p className="text-sm max-w-xl" style={{ color: 'var(--olive)' }}>{tpl.description}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={doPreview} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="st-preview-btn"><Eye size={15} /> Preview</button>
                  {tpl.is_custom && <button onClick={doReset} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-title" style={{ background: 'white', color: 'var(--maroon)', border: '1px solid var(--maroon)' }} data-testid="st-reset-btn"><RotateCcw size={15} /> Reset</button>}
                  <button onClick={doSave} disabled={busy || !dirty} className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: (busy || !dirty) ? 0.6 : 1 }} data-testid="st-save-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
                </div>
              </div>

              {/* Placeholders */}
              <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.03)' }}>
                <div className="flex items-center gap-1.5 text-xs font-title mb-2" style={{ color: 'var(--navy)' }}><Tag size={13} /> Placeholders — click to copy</div>
                <div className="flex flex-wrap gap-1.5">
                  {(tpl.fields || []).map((f) => (
                    <button key={f.token} onClick={() => copyToken(f.token)} title={f.label} data-testid={`st-token-${f.token}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-mono" style={{ background: 'white', color: 'var(--maroon)', border: '1px solid rgba(0,0,0,0.1)' }}>
                      <Copy size={10} /> {`{{${f.token}}}`}
                    </button>
                  ))}
                  {(tpl.datasets || []).map((d) => (
                    <span key={d.key} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} title={`Repeats: ${d.fields.join(', ')}`}>
                      list: {`{{${d.key}}}`} → {d.fields.map((x) => `{{${x}}}`).join(' ')}
                    </span>
                  ))}
                </div>
              </div>

              {tpl.category === 'email' && (
                <div>
                  <label className="text-sm font-title block mb-1" style={{ color: 'var(--navy)' }}>Subject line</label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="st-subject" />
                </div>
              )}

              <div>
                <label className="text-sm font-title block mb-1" style={{ color: 'var(--navy)' }}>Body</label>
                <RichTextEditor value={body} onChange={setBody} fields={tpl.fields || []} datasets={tpl.datasets || []} />
              </div>

              {preview && (
                <div data-testid="st-preview">
                  <div className="text-sm font-title mb-1 flex items-center gap-2" style={{ color: 'var(--navy)' }}><Check size={14} style={{ color: 'var(--maroon)' }} /> Live preview (with sample data)</div>
                  {preview.subject != null && preview.category === 'email' && <div className="text-xs mb-2 px-3 py-2 rounded" style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--olive)' }}><strong>Subject:</strong> {preview.subject}</div>}
                  <iframe title="template-preview" srcDoc={preview.html} className="w-full rounded-lg border" style={{ height: 460, borderColor: 'rgba(0,0,0,0.12)', background: '#fff' }} data-testid="st-preview-frame" />
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
